import { useState, useEffect, useRef } from 'react'
import { getAlert, saveAlert, deleteAlert } from '../api'
import { useProfiles } from '../context/ProfileContext'
import './AlertModal.css'

const CATEGORY_GROUPS = [
    {
        label: 'Tops', value: 'tops',
        children: [
            { label: 'T-Shirt', value: 'tshirt' },
            { label: 'Casual Shirt', value: 'casual_shirt' },
            { label: 'Dress Shirt', value: 'dress_shirt' },
            { label: 'Polo', value: 'polo' },
            { label: 'Sweater', value: 'sweater' },
            { label: 'Hoodie & Sweatshirt', value: 'hoodie' },
            { label: 'Activewear Tops', value: 'activewear_tops' },
        ]
    },
    {
        label: 'Outerwear', value: 'outerwear',
        children: [
            { label: 'Coats, Jackets & Vests', value: 'coats_jackets' },
            { label: 'Activewear Jackets', value: 'activewear_jackets' },
            { label: 'Tracksuits & Sets', value: 'tracksuits' },
        ]
    },
    {
        label: 'Bottoms', value: 'bottoms',
        children: [
            { label: 'Pants', value: 'pants' },
            { label: 'Jeans', value: 'jeans' },
            { label: 'Shorts', value: 'shorts' },
            { label: 'Swimwear', value: 'swimwear' },
        ]
    },
    {
        label: 'Footwear', value: 'footwear',
        children: [
            { label: 'Sneakers', value: 'sneakers' },
            { label: 'Boots', value: 'boots' },
            { label: 'Casual Shoes', value: 'casual_shoes' },
            { label: 'Dress Shoes & Loafers', value: 'dress_shoes_loafers' },
        ]
    },
    {
        label: 'Other', value: 'other',
        children: [
            { label: 'Suits', value: 'suits' },
            { label: 'Other Activewear', value: 'other_activewear' },
        ]
    },
]

function CategorySelect({ selectedCategories, setSelectedCategories }) {
    const [expandedParents, setExpandedParents] = useState([])

    const allChildValues = (group) => group.children.map(c => c.value)

    const toggleParent = (group) => {
        const children = allChildValues(group)
        const allSelected = children.every(v => selectedCategories.includes(v))
        if (allSelected) {
            setSelectedCategories(selectedCategories.filter(v => !children.includes(v)))
        } else {
            setSelectedCategories([...new Set([...selectedCategories, ...children])])
        }
    }

    const toggleChild = (childValue) => {
        if (selectedCategories.includes(childValue)) {
            setSelectedCategories(selectedCategories.filter(v => v !== childValue))
        } else {
            setSelectedCategories([...selectedCategories, childValue])
        }
    }

    const toggleExpand = (e, groupValue) => {
        e.stopPropagation()
        setExpandedParents(prev =>
            prev.includes(groupValue) ? prev.filter(p => p !== groupValue) : [...prev, groupValue]
        )
    }

    const isParentChecked = (group) =>
        allChildValues(group).every(v => selectedCategories.includes(v))

    const isParentIndeterminate = (group) => {
        const children = allChildValues(group)
        const count = children.filter(v => selectedCategories.includes(v)).length
        return count > 0 && count < children.length
    }

    return (
        <div className="alert-category-list">
            {CATEGORY_GROUPS.map(group => {
                const expanded = expandedParents.includes(group.value)
                const checked = isParentChecked(group)
                const indeterminate = isParentIndeterminate(group)
                return (
                    <div key={group.value}>
                        <label className="alert-category-parent">
                            <input
                                type="checkbox"
                                checked={checked}
                                ref={el => { if (el) el.indeterminate = indeterminate }}
                                onChange={() => toggleParent(group)}
                            />
                            <span className="alert-category-parent-label">{group.label}</span>
                            <button
                                type="button"
                                className="alert-category-expand"
                                onClick={(e) => toggleExpand(e, group.value)}
                                aria-label={expanded ? 'Collapse' : 'Expand'}
                            >
                                {expanded ? '▲' : '▼'}
                            </button>
                        </label>
                        {expanded && (
                            <div className="alert-category-children">
                                {group.children.map(child => (
                                    <label key={child.value} className="alert-category-child">
                                        <input
                                            type="checkbox"
                                            checked={selectedCategories.includes(child.value)}
                                            onChange={() => toggleChild(child.value)}
                                        />
                                        {child.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

function BrandSearch({ allBrands, selectedBrandIds, setSelectedBrandIds, likedBrandIds = [] }) {
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        function handleClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    const savedBrands = allBrands.filter(b => likedBrandIds.includes(b.id))
    const otherBrands = allBrands.filter(b => !likedBrandIds.includes(b.id))

    const filtered = query.trim()
        ? allBrands.filter(b => b.name.toLowerCase().includes(query.toLowerCase()))
        : null

    const selectedBrands = allBrands.filter(b => selectedBrandIds.includes(b.id))

    const toggleBrand = (id) => {
        if (selectedBrandIds.includes(id)) {
            setSelectedBrandIds(selectedBrandIds.filter(b => b !== id))
        } else {
            setSelectedBrandIds([...selectedBrandIds, id])
        }
    }

    return (
        <div className="alert-brand-search" ref={ref}>
            <input
                type="text"
                className="alert-brand-input"
                placeholder="Search brands..."
                value={query}
                onChange={e => { setQuery(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
            />
            {open && (
                <div className="alert-brand-dropdown">
                    {filtered ? (
                        filtered.length > 0 ? filtered.map(b => (
                            <label key={b.id} className="alert-brand-option">
                                <input type="checkbox" checked={selectedBrandIds.includes(b.id)} onChange={() => toggleBrand(b.id)} />
                                {b.name}
                            </label>
                        )) : <div className="alert-brand-empty">No brands match</div>
                    ) : (
                        <>
                            {savedBrands.length > 0 && (
                                <>
                                    <div className="alert-brand-group-label">Your saved brands</div>
                                    {savedBrands.map(b => (
                                        <label key={b.id} className="alert-brand-option">
                                            <input type="checkbox" checked={selectedBrandIds.includes(b.id)} onChange={() => toggleBrand(b.id)} />
                                            {b.name}
                                        </label>
                                    ))}
                                    <div className="alert-brand-divider" />
                                    <div className="alert-brand-group-label">All brands</div>
                                </>
                            )}
                            {otherBrands.map(b => (
                                <label key={b.id} className="alert-brand-option">
                                    <input type="checkbox" checked={selectedBrandIds.includes(b.id)} onChange={() => toggleBrand(b.id)} />
                                    {b.name}
                                </label>
                            ))}
                        </>
                    )}
                </div>
            )}
            {selectedBrands.length > 0 && (
                <div className="alert-brand-tags">
                    {selectedBrands.map(b => (
                        <span key={b.id} className="alert-tag">
                            {b.name}
                            <button type="button" onClick={() => toggleBrand(b.id)} aria-label={`Remove ${b.name}`}>×</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}

export default function AlertModal({ user, allBrands, onClose }) {
    const { likedBrandIds } = useProfiles()
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [hasExisting, setHasExisting] = useState(false)
    const [savedMessage, setSavedMessage] = useState('')

    const [selectedBrandIds, setSelectedBrandIds] = useState([])
    const [selectedCategories, setSelectedCategories] = useState([])
    const [condition, setCondition] = useState('')
    const [keywords, setKeywords] = useState([])
    const [keywordInput, setKeywordInput] = useState('')

    useEffect(() => {
        if (!user) return
        setLoading(true)
        getAlert(user.id).then(alert => {
            if (alert) {
                setHasExisting(true)
                setSelectedBrandIds(alert.brand_ids || [])
                setSelectedCategories(alert.categories || [])
                setCondition(alert.condition || '')
                setKeywords(alert.keywords || [])
            }
        }).finally(() => setLoading(false))
    }, [user])

    const addKeyword = () => {
        const kw = keywordInput.trim()
        if (kw && !keywords.includes(kw)) {
            setKeywords([...keywords, kw])
        }
        setKeywordInput('')
    }

    const removeKeyword = (kw) => setKeywords(keywords.filter(k => k !== kw))

    const handleSave = async () => {
        if (!user) return
        setSaving(true)
        await saveAlert({
            user_id: user.id,
            email: user.email,
            brand_ids: selectedBrandIds,
            categories: selectedCategories,
            condition: condition || null,
            keywords,
        })
        setSaving(false)
        setHasExisting(true)
        setSavedMessage('Alert saved!')
        setTimeout(() => setSavedMessage(''), 2000)
    }

    const handleDelete = async () => {
        if (!user) return
        setDeleting(true)
        await deleteAlert(user.id)
        setDeleting(false)
        setHasExisting(false)
        setSelectedBrandIds([])
        setSelectedCategories([])
        setCondition('')
        setKeywords([])
        setSavedMessage('Alert removed.')
        setTimeout(() => setSavedMessage(''), 2000)
    }

    return (
        <div className="alert-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
            <div className="alert-modal">
                <div className="alert-modal-header">
                    <span className="alert-modal-title">Daily alert</span>
                    <button type="button" className="alert-modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                {!user ? (
                    <div className="alert-signin-note">
                        Sign in to set up alerts.
                    </div>
                ) : loading ? (
                    <div className="alert-loading">Loading...</div>
                ) : (
                    <>
                        <div className="alert-section">
                            <div className="alert-section-label">Brands</div>
                            <BrandSearch
                                allBrands={allBrands}
                                selectedBrandIds={selectedBrandIds}
                                setSelectedBrandIds={setSelectedBrandIds}
                                likedBrandIds={likedBrandIds}
                            />
                            {selectedBrandIds.length === 0 && (
                                <p className="alert-hint">No brands selected — all brands will be included.</p>
                            )}
                        </div>

                        <div className="alert-section">
                            <div className="alert-section-label">Category</div>
                            <CategorySelect
                                selectedCategories={selectedCategories}
                                setSelectedCategories={setSelectedCategories}
                            />
                            {selectedCategories.length === 0 && (
                                <p className="alert-hint">No category selected — all categories included.</p>
                            )}
                        </div>

                        <div className="alert-section">
                            <div className="alert-section-label">Condition</div>
                            <div className="alert-condition-row">
                                {['', 'new', 'pre-owned'].map(val => (
                                    <button
                                        key={val}
                                        type="button"
                                        className={`alert-condition-btn${condition === val ? ' active' : ''}`}
                                        onClick={() => setCondition(val)}
                                    >
                                        {val === '' ? 'Any' : val === 'new' ? 'New' : 'Pre-owned'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="alert-section">
                            <div className="alert-section-label">Keywords</div>
                            <div className="alert-keyword-tags">
                                {keywords.map(kw => (
                                    <span key={kw} className="alert-tag">
                                        {kw}
                                        <button type="button" onClick={() => removeKeyword(kw)} aria-label={`Remove ${kw}`}>×</button>
                                    </span>
                                ))}
                            </div>
                            <input
                                type="text"
                                className="alert-keyword-input"
                                placeholder="Add keyword and press Enter..."
                                value={keywordInput}
                                onChange={e => setKeywordInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword() } }}
                            />
                            <p className="alert-hint">Match any keyword against listing title. Each keyword is matched independently.</p>
                        </div>

                        <p className="alert-note">Sent daily when new items are posted. No email if nothing matches.</p>

                        <div className="alert-actions">
                            {hasExisting && (
                                <button
                                    type="button"
                                    className="alert-delete-btn"
                                    onClick={handleDelete}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Removing...' : 'Remove alert'}
                                </button>
                            )}
                            <button
                                type="button"
                                className="alert-save-btn"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : hasExisting ? 'Update alert' : 'Save alert'}
                            </button>
                        </div>

                        {savedMessage && (
                            <p className="alert-saved-message">{savedMessage}</p>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
